import { createI18n } from 'vue-i18n';
import { i18nCommon, loadCommonLocale } from '@r7e/shared'; 
import { authorEn } from './locales/author.en';

const deepMerge = (obj1, obj2) => {
  const result = { ...obj1 };
  for (const key in obj2) {
    if (
      Object.prototype.hasOwnProperty.call(obj2, key)
      && obj2[key] !== null
      && typeof obj2[key] === 'object'
      && !Array.isArray(obj2[key])
      && obj1[key] !== undefined
      && typeof obj1[key] === 'object'
      && !Array.isArray(obj1[key])
    ) {
      result[key] = deepMerge(obj1[key], obj2[key]);
    } else {
      result[key] = obj2[key];
    }
  }
  return result;
};

// Cache for loaded app-specific locales
const loadedAppLocales = {
  en: authorEn,
};

// Lazy load app-specific locale function
async function loadAppLocale(locale) {
  // Return cached locale if already loaded
  if (loadedAppLocales[locale]) {
    return loadedAppLocales[locale];
  }

  // Load locale dynamically
  let localeModule;
  switch (locale) {
    case 'fr':
      localeModule = await import('./locales/author.fr');
      loadedAppLocales.fr = localeModule.authorFr;
      return loadedAppLocales.fr;
    case 'de':
      localeModule = await import('./locales/author.de');
      loadedAppLocales.de = localeModule.authorDe;
      return loadedAppLocales.de;
    case 'es':
      localeModule = await import('./locales/author.es');
      loadedAppLocales.es = localeModule.authorEs;
      return loadedAppLocales.es;
    case 'it':
      localeModule = await import('./locales/author.it');
      loadedAppLocales.it = localeModule.authorIt;
      return loadedAppLocales.it;
    case 'ja':
      localeModule = await import('./locales/author.ja');
      loadedAppLocales.ja = localeModule.authorJa;
      return loadedAppLocales.ja;
    case 'zh':
      localeModule = await import('./locales/author.zh');
      loadedAppLocales.zh = localeModule.authorZh;
      return loadedAppLocales.zh;
    case 'en':
      return loadedAppLocales.en;
    default:
      return loadedAppLocales.en; // Fallback to English
  }
}

// Initialize messages with only English loaded
const messages = {
  en: deepMerge(i18nCommon.en || {}, authorEn),
};

// Always initialize with 'en' - saved locale will be loaded in main.js
const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages,
});

// Function to load a locale on demand
export async function loadLocale(locale) {
  // If locale is already loaded, just switch to it
  if (i18n.global.availableLocales.includes(locale)) {
    i18n.global.locale.value = locale;
    return;
  }

  // Load both common and app-specific locales
  const [commonLocale, appLocale] = await Promise.all([
    loadCommonLocale(locale),
    loadAppLocale(locale),
  ]);

  // Merge using deepMerge and set the locale messages
  i18n.global.setLocaleMessage(locale, deepMerge(commonLocale, appLocale));

  // Switch to the new locale
  i18n.global.locale.value = locale;
}

export default i18n; 